import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/reducers';
import { fetchUsersRequest } from '../store/users/actions';
import { fetchAdminDashboardRequest } from '../store/decisions/actions';
import {
  Users,
  Clock,
  CheckCircle,
  ArrowRight,
  UserX,
  Hourglass,
  ShieldCheck,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import './AdminDashboard.css';

const actionLabels: Record<string, string> = {
  upload: 'a uploadé',
  extraction_api: 'extraction OCR',
  anonymisation: 'anonymisation',
  resume_genere: 'résumé généré',
  validation: 'validation',
  publication: 'publication',
  rejet: 'rejet',
  modification: 'modification',
  soumettre: 'soumission',
};

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { users } = useSelector((state: RootState) => state.users);
  const { adminDashboard, adminDashboardLoading } = useSelector((state: RootState) => state.decisions);

  useEffect(() => {
    dispatch(fetchUsersRequest({ limit: 200 }));
    dispatch(fetchAdminDashboardRequest());
  }, [dispatch]);

  // Display name
  const displayName = user?.first_name
    ? `${user.first_name.charAt(0)}. ${user.last_name || ''}`
    : user?.email || 'Admin';

  // KPI data — from /decisions/dashboard/ API
  const activeUsers = adminDashboard?.utilisateurs?.actifs ?? users.filter((u) => u.is_active).length;
  const totalUsers = (activeUsers + (adminDashboard?.alertes?.agents_inactifs?.length || 0)) || users.length;
  const pendingCount = adminDashboard?.decisions?.en_attente ?? 0;
  const publishedThisMonth = adminDashboard?.decisions?.publiees_ce_mois ?? 0;
  const validatedCount = adminDashboard?.decisions?.validees ?? 0;

  // Top agents data — map API fields (nom/total) to chart fields (name/count)
  const topAgents = useMemo(() => {
    const raw = adminDashboard?.top_agents || [];
    return raw.map((agent: any) => {
      // Build short name: "Prénom N." from full name
      const fullName = agent.nom || agent.name || '';
      const parts = fullName.split(' ').filter(Boolean);
      let shortName = fullName;
      if (parts.length >= 2) {
        // Take first part + initial of second
        shortName = `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
      }
      return {
        name: shortName,
        fullName,
        count: agent.total ?? agent.count ?? 0,
      };
    });
  }, [adminDashboard]);

  const barColors = ['#8B6914', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6'];

  // Alerts — from dashboard API (alertes is an object)
  const alertesData = adminDashboard?.alertes;
  const inactiveAgents = alertesData?.agents_inactifs?.length ?? 0;
  const oldPending = alertesData?.decisions_en_retard ?? adminDashboard?.decisions?.en_retard ?? 0;

  // Recent activity from adminDashboard
  const recentActivity: any[] = adminDashboard?.activite_recente || [];

  const formatActivityTime = (dateStr: string) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return "À l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `Il y a ${diffH}h`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `Il y a ${diffD}j`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="admin-dash">
      {/* Welcome banner */}
      <div className="admin-dash-welcome">
        <div>
          <h2>Bonjour {displayName} &#128075;</h2>
          <p>
            Bienvenue dans l'espace d'administration du Centre National de
            Documentation Juridique.
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="admin-dash-kpis">
        <div className="admin-kpi-card">
          <div className="admin-kpi-icon admin-kpi-icon-blue">
            <Users size={22} />
          </div>
          <div className="admin-kpi-info">
            <span className="admin-kpi-value">{activeUsers}</span>
            <span className="admin-kpi-label">Utilisateurs actifs</span>
            <span className="admin-kpi-sub">sur {totalUsers} comptes</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="admin-kpi-icon admin-kpi-icon-orange">
            <Clock size={22} />
          </div>
          <div className="admin-kpi-info">
            <span className="admin-kpi-value">{pendingCount}</span>
            <span className="admin-kpi-label">Décisions en attente</span>
            <span className="admin-kpi-sub">à vérifier</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="admin-kpi-icon admin-kpi-icon-teal">
            <ShieldCheck size={22} />
          </div>
          <div className="admin-kpi-info">
            <span className="admin-kpi-value">{validatedCount}</span>
            <span className="admin-kpi-label">Décisions validées</span>
            <span className="admin-kpi-sub">total validées</span>
          </div>
        </div>

        <div className="admin-kpi-card">
          <div className="admin-kpi-icon admin-kpi-icon-green">
            <CheckCircle size={22} />
          </div>
          <div className="admin-kpi-info">
            <span className="admin-kpi-value">{publishedThisMonth}</span>
            <span className="admin-kpi-label">Publiées ce mois</span>
            <span className="admin-kpi-sub">décisions publiées</span>
          </div>
        </div>
      </div>

      {/* Middle row: Top agents + Alerts */}
      <div className="admin-dash-middle">
        {/* Top agents chart */}
        <div className="admin-dash-panel admin-dash-agents">
          <div className="admin-dash-panel-header">
            <div>
              <h3>Top agents par volume traité</h3>
              <span className="admin-dash-panel-sub">Classement global</span>
            </div>
            <button
              className="admin-dash-link"
              onClick={() => navigate('/espace-admin/utilisateurs')}
            >
              Tous les utilisateurs <ArrowRight size={14} />
            </button>
          </div>
          <div className="admin-agents-chart">
            {topAgents.length > 0 ? (
              <ResponsiveContainer width="100%" height={topAgents.length * 48 + 20}>
                <BarChart
                  data={topAgents}
                  layout="vertical"
                  margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                >
                  <XAxis type="number" hide />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={120}
                    tick={{ fontSize: 12, fontWeight: 600, fill: '#333' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    formatter={(value: any, _name: any, props: any) => [
                      `${value} décisions`,
                      props.payload?.fullName || 'Agent',
                    ]}
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid #f0f0f0',
                      fontSize: 13,
                    }}
                  />
                  <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={24}>
                    {topAgents.map((_: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={barColors[index % barColors.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="admin-agents-empty">
                {adminDashboardLoading ? 'Chargement...' : 'Aucune donnée disponible'}
              </div>
            )}
          </div>
        </div>

        {/* Alerts panel */}
        <div className="admin-dash-panel admin-dash-alerts">
          <div className="admin-dash-panel-header">
            <h3>Alertes</h3>
          </div>
          <div className="admin-alerts-list">
            {inactiveAgents > 0 && (
              <div className="admin-alert-item admin-alert-orange">
                <div className="admin-alert-icon">
                  <UserX size={18} />
                </div>
                <div className="admin-alert-content">
                  <span className="admin-alert-title">
                    {inactiveAgents} agent{inactiveAgents > 1 ? 's' : ''} inactif
                    {inactiveAgents > 1 ? 's' : ''}
                  </span>
                  <span className="admin-alert-desc">
                    Comptes désactivés ou sans connexion récente
                  </span>
                </div>
              </div>
            )}

            {oldPending > 0 && (
              <div className="admin-alert-item admin-alert-pink">
                <div className="admin-alert-icon">
                  <Hourglass size={18} />
                </div>
                <div className="admin-alert-content">
                  <span className="admin-alert-title">
                    {oldPending} décision{oldPending > 1 ? 's' : ''} en retard
                  </span>
                  <span className="admin-alert-desc">
                    Décisions en attente depuis trop longtemps
                  </span>
                </div>
              </div>
            )}

            {inactiveAgents === 0 && oldPending === 0 && (
              <div className="admin-alerts-empty">
                Aucune alerte pour le moment.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Activity feed */}
      <div className="admin-dash-panel admin-dash-activity">
        <div className="admin-dash-panel-header">
          <h3>Activité récente du centre</h3>
          <span className="admin-activity-badge">Temps réel</span>
        </div>
        <div className="admin-activity-list">
          {recentActivity.length > 0 ? (
            recentActivity.slice(0, 10).map((item, idx) => (
              <div key={idx} className="admin-activity-item">
                <div className="admin-activity-dot" />
                <div className="admin-activity-content">
                  <span className="admin-activity-text">
                    <strong>{item.agent || 'Système'}</strong>{' '}
                    {actionLabels[item.action] || item.action}
                    {item.decision_numero && (
                      <> — <span className="admin-activity-ref">{item.decision_numero}</span></>
                    )}
                  </span>
                  <span className="admin-activity-time">
                    {formatActivityTime(item.created_at)}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="admin-activity-empty">
              {adminDashboardLoading ? 'Chargement...' : 'Aucune activité récente.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
